import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 4-2: Underground, brick structures, many gaps
export function buildLevel4_2(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [12, 14],
    [28, 30],
    [44, 46],
    [60, 62],
    [76, 78],
    [92, 94],
    [108, 110],
    [124, 126],
    [140, 142],
    [156, 158],
    [172, 174]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  setTile(6, 4, TileType.Question, { kind: "mushroom" });

  const pipes: Array<[number, number]> = [
      [18, 2],
    [32, 3],
    [48, 4],
    [64, 2],
    [80, 3],
    [96, 4],
    [112, 2],
    [128, 3],
    [144, 4],
    [160, 2],
    [176, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // brick structures
  for (let x = 20; x <= 24; x++) setTile(x, 4, TileType.Brick);
  setTile(22, 4, TileType.Question, { kind: "coin" });

  for (let x = 34; x <= 38; x++) setTile(x, 4, TileType.Brick);
  setTile(36, 4, TileType.Question, { kind: "mushroom" });
  for (let x = 35; x <= 37; x++) setTile(x, 8, TileType.Brick);
  setTile(36, 8, TileType.Question, { kind: "coin" });

  for (let x = 50; x <= 54; x++) setTile(x, 4, TileType.Brick);
  setTile(52, 4, TileType.Question, { kind: "coin" });

  for (let x = 66; x <= 70; x++) setTile(x, 4, TileType.Brick);
  setTile(68, 4, TileType.Question, { kind: "mushroom" });
  for (let x = 67; x <= 69; x++) setTile(x, 8, TileType.Brick);
  setTile(68, 8, TileType.Question, { kind: "coin" });

  for (let x = 82; x <= 86; x++) setTile(x, 4, TileType.Brick);
  setTile(84, 4, TileType.Question, { kind: "coin" });

  for (let x = 98; x <= 102; x++) setTile(x, 4, TileType.Brick);
  setTile(100, 4, TileType.Question, { kind: "coin" });
  for (let x = 99; x <= 101; x++) setTile(x, 8, TileType.Brick);
  setTile(100, 8, TileType.Question, { kind: "mushroom" });

  for (let x = 114; x <= 118; x++) setTile(x, 4, TileType.Brick);
  setTile(116, 4, TileType.Question, { kind: "coin" });

  for (let x = 130; x <= 134; x++) setTile(x, 4, TileType.Brick);
  setTile(132, 4, TileType.Question, { kind: "coin" });

  for (let x = 146; x <= 150; x++) setTile(x, 4, TileType.Brick);
  setTile(148, 4, TileType.Question, { kind: "mushroom" });
  for (let x = 147; x <= 149; x++) setTile(x, 8, TileType.Brick);
  setTile(148, 8, TileType.Question, { kind: "coin" });

  for (let x = 162; x <= 166; x++) setTile(x, 4, TileType.Brick);
  setTile(164, 4, TileType.Question, { kind: "coin" });

  for (let x = 178; x <= 182; x++) setTile(x, 4, TileType.Brick);
  setTile(180, 4, TileType.Question, { kind: "coin" });

  stair(184, 1);
  stair(185, 2);
  stair(186, 3);
  stair(187, 4);
  stair(188, 4);
  stair(189, 4);
  stair(190, 3);
  stair(191, 2);
  stair(192, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 8 },
    { kind: "koopa", x: 16 },
    { kind: "goomba", x: 22 },
    { kind: "goomba", x: 30 },
    { kind: "koopa", x: 38 },
    { kind: "goomba", x: 46 },
    { kind: "goomba", x: 54 },
    { kind: "koopa", x: 62 },
    { kind: "goomba", x: 70 },
    { kind: "goomba", x: 78 },
    { kind: "koopa", x: 86 },
    { kind: "goomba", x: 94 },
    { kind: "goomba", x: 102 },
    { kind: "koopa", x: 110 },
    { kind: "goomba", x: 118 },
    { kind: "goomba", x: 126 },
    { kind: "koopa", x: 134 },
    { kind: "goomba", x: 142 },
    { kind: "goomba", x: 150 },
    { kind: "koopa", x: 158 },
    { kind: "goomba", x: 166 },
    { kind: "goomba", x: 174 },
    { kind: "koopa", x: 182 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
